import { API_BASE_URL } from '../config/api';

interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('token');
    // Use salonId for X-Tenant-ID (backend filters by salonId). Fallback to tenantId or env default.
    const tenantHeader =
      localStorage.getItem('salonId') ||
      localStorage.getItem('tenantId') ||
      import.meta.env.VITE_TENANT_ID ||
      import.meta.env.VITE_DEFAULT_TENANT_ID ||
      '';

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }

    // Add tenant/salon ID header for multi-tenant support
    defaultHeaders['X-Tenant-ID'] = tenantHeader;

    const config: RequestInit = {
      ...options,
      // Never read an API response from the HTTP cache. The API sends an ETag
      // with no Cache-Control, so browsers may treat a stored response as fresh
      // and skip the network entirely — which made a refetch after a mutation
      // (e.g. deactivating staff) re-render the pre-change list.
      cache: 'no-store',
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorMessage = 'API request failed';
        try {
          const errorData = await response.json();
          errorMessage =
            errorData.error?.message || errorData.message || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // For successful responses, return the full response object
      // This preserves pagination and other metadata
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
